import { Pomodoro } from '../types/pomodoro';
import { Task } from '../types/task';
import { pricingService, UsageStats } from './PricingService';
import { userDataAggregator, ComprehensiveUserData } from './UserDataAggregator';

interface ProductivityPattern {
  peakHours: { hour: number; productivity: number }[];
  bestDays: { day: string; productivity: number }[];
  taskCompletion: { category: string; rate: number }[];
  focusPatterns: { pattern: string; effectiveness: number }[];
}

interface TaskSuggestion {
  title: string;
  priority: 'low' | 'medium' | 'high';
  estimatedPomodoros: number;
  bestTimeSlot: string;
  reasoning: string;
}

interface AdvancedAnalytics {
  productivityScore: number;
  focusQuality: number;
  taskEfficiency: number;
  workLifeBalance: number;
  burnoutRisk: number;
  suggestions: string[];
}

interface FocusAnalysis {
  overallQuality: number;
  distractionPatterns: {
    timeOfDay: { hour: number; distractions: number }[];
    commonInterruptions: { type: string; count: number }[];
    focusStreaks: { length: number; frequency: number }[];
  };
  recommendations: {
    timeBlocks: { start: number; end: number; quality: number }[];
    environmentalFactors: string[];
    techniques: string[];
  };
}

interface BreakSuggestion {
  duration: number;
  type: 'short' | 'long';
  activity: string;
  reason: string;
  energyImpact: number;
}

interface FocusZone {
  startHour: number;
  endHour: number;
  successRate: number;
  distractionLevel: 'low' | 'medium' | 'high';
  energyLevel: 'peak' | 'moderate' | 'low';
  recommendedActivities: string[];
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
}

interface ChatContext {
  recentPomodoros: Pomodoro[];
  incompleteTasks: Task[];
  completedTasks: Task[];
  projects: Array<{ id: string; name: string; color: string }>;
  timerSettings: {
    workDuration: number;
    shortBreakDuration: number;
    longBreakDuration: number;
    longBreakInterval: number;
  };
  currentTimerState: {
    phase: 'work' | 'shortBreak' | 'longBreak';
    isActive: boolean;
    completedPomodoros: number;
  };
  productivityPatterns: ProductivityPattern;
  currentTime: number;
}


class AIService {
  private apiKey: string | null = null;
  private baseUrl = 'https://api.deepseek.com/v1';
  private userId: string | null = null;

  constructor() {
    this.apiKey = import.meta.env.VITE_DEEPSEEK_API_KEY;
  }

  setUserId(userId: string) {
    this.userId = userId;
  }

  // Test method to verify API connection
  async testConnection(): Promise<boolean> {
    if (!this.apiKey) {
      console.error('No API key available');
      return false;
    }

    try {
      const response = await fetch(`${this.baseUrl}/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: 'deepseek-chat',
          messages: [{ role: 'user', content: 'Hello' }],
          max_tokens: 10
        })
      });

      await response.json();
      return response.ok;
    } catch (error) {
      console.error('DeepSeek API test failed:', error);
      return false;
    }
  }

  private async checkUsageAndLimits(): Promise<void> {
    if (!this.userId) {
      throw new Error('User not authenticated');
    }

    const { allowed, reason } = await pricingService.checkUsageLimit(this.userId);
    if (!allowed) {
      throw new Error(reason);
    }
  }

  private async callDeepSeekAPI(prompt: string): Promise<string> {
    if (!this.apiKey) {
      throw new Error('DeepSeek API key not configured');
    }

    if (!this.userId) {
      throw new Error('User not authenticated');
    }

    // Check usage limits before making the API call
    await this.checkUsageAndLimits();

    try {
      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout

      const response = await fetch(`${this.baseUrl}/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: 'deepseek-chat',
          messages: [{
            role: 'user',
            content: prompt
          }],
          max_tokens: 2000, // Increased for comprehensive analysis
          temperature: 0.7
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('DeepSeek API error:', errorText);
        throw new Error(`API call failed: ${response.statusText} - ${errorText}`);
      }

      const responseData = await response.json();
      
      // Record successful usage
      await pricingService.recordUsage(this.userId);
      
      return responseData.choices[0].message.content;
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.error('DeepSeek API call timed out after 15 seconds');
        throw new Error('AI analysis timed out. Please try again.');
      }
      console.error('Error calling DeepSeek API:', error);
      throw error;
    }
  }

  private async getAIResponse<T>(prompt: string, fallback: () => T): Promise<T> {
    if (this.apiKey) {
      try {
        const response = await this.callDeepSeekAPI(prompt);
        
        // Extract JSON from markdown code blocks if present
        let jsonString = response.trim();
        
        // Remove markdown code block syntax
        if (jsonString.startsWith('```json')) {
          jsonString = jsonString.replace(/^```json\s*/, '').replace(/\s*```$/, '');
        } else if (jsonString.startsWith('```')) {
          jsonString = jsonString.replace(/^```\s*/, '').replace(/\s*```$/, '');
        }
        
        // Try to find JSON object/array in the response
        const jsonMatch = jsonString.match(/(\{[\s\S]*\}|\[[\s\S]*\])/);
        if (jsonMatch) {
          jsonString = jsonMatch[1];
        }
        
        return JSON.parse(jsonString);
      } catch (error) {
        console.error('AI service error:', error);
        return fallback();
      }
    }
    return fallback();
  }

  async analyzeProductivityPatterns(
    pomodoros: Pomodoro[],
    tasks: Task[]
  ): Promise<ProductivityPattern> {
    const prompt = `
      Analyze these productivity patterns:
      Pomodoros: ${JSON.stringify(pomodoros)}
      Tasks: ${JSON.stringify(tasks)}

      Provide analysis in this JSON format:
      {
        "peakHours": [{"hour": number, "productivity": number}],
        "bestDays": [{"day": string, "productivity": number}],
        "taskCompletion": [{"category": string, "rate": number}],
        "focusPatterns": [{"pattern": string, "effectiveness": number}]
      }
    `;

    return this.getAIResponse(prompt, () => this.generateBasicProductivityPattern(pomodoros, tasks));
  }

  async getAdvancedAnalytics(
    pomodoros: Pomodoro[],
    tasks: Task[]
  ): Promise<AdvancedAnalytics> {
    const prompt = `
      Analyze productivity data and provide insights:
      Pomodoros: ${JSON.stringify(pomodoros)}
      Tasks: ${JSON.stringify(tasks)}

      Return analysis in this JSON format:
      {
        "productivityScore": number,
        "focusQuality": number,
        "taskEfficiency": number,
        "workLifeBalance": number,
        "burnoutRisk": number,
        "suggestions": string[]
      }
    `;

    return this.getAIResponse(prompt, () => this.generateBasicAnalytics(pomodoros, tasks));
  }

  async analyzeFocusQuality(pomodoros: Pomodoro[]): Promise<FocusAnalysis> {
    const prompt = `
      Analyze focus quality from these pomodoro sessions:
      ${JSON.stringify(pomodoros)}

      Provide analysis in this JSON format:
      {
        "overallQuality": number,
        "distractionPatterns": {
          "timeOfDay": [{"hour": number, "distractions": number}],
          "commonInterruptions": [{"type": string, "count": number}],
          "focusStreaks": [{"length": number, "frequency": number}]
        },
        "recommendations": {
          "timeBlocks": [{"start": number, "end": number, "quality": number}],
          "environmentalFactors": string[],
          "techniques": string[]
        }
      }
    `;

    return this.getAIResponse(prompt, () => this.generateBasicFocusAnalysis(pomodoros));
  }

  async suggestBreak(
    currentSession: Pomodoro | null,
    recentSessions: Pomodoro[],
    currentHour: number
  ): Promise<BreakSuggestion | null> {
    const prompt = `
      Suggest a break based on:
      Current Session: ${JSON.stringify(currentSession)}
      Recent Sessions: ${JSON.stringify(recentSessions)}
      Current Hour: ${currentHour}

      Provide suggestion in this JSON format:
      {
        "duration": number,
        "type": "short" | "long",
        "activity": string,
        "reason": string,
        "energyImpact": number
      }
    `;

    return this.getAIResponse(prompt, () => this.generateBasicBreakSuggestion(currentSession, recentSessions, currentHour));
  }

  async detectFocusZones(pomodoros: Pomodoro[]): Promise<FocusZone[]> {
    const prompt = `
      Analyze these pomodoro sessions to identify focus zones:
      ${JSON.stringify(pomodoros)}

      Return zones in this JSON format:
      [{
        "startHour": number,
        "endHour": number,
        "successRate": number,
        "distractionLevel": "low" | "medium" | "high",
        "energyLevel": "peak" | "moderate" | "low",
        "recommendedActivities": string[]
      }]
    `;

    return this.getAIResponse(prompt, () => this.generateBasicFocusZones(pomodoros));
  }

  async generateComprehensiveChatResponse(
    message: string,
    userId: string
  ): Promise<ChatMessage> {
    try {
      // Get comprehensive user data
      const userData = await userDataAggregator.getComprehensiveUserData(userId);
      
      // Create a comprehensive prompt with ALL user data
      const prompt = this.createComprehensivePrompt(message, userData);
      
      if (this.apiKey && this.userId) {
        const response = await this.callDeepSeekAPI(prompt);
        return {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: response,
          timestamp: Date.now()
        };
      }
      
      return this.generateBasicComprehensiveResponse(message, userData);
    } catch (error) {
      console.error('Error generating comprehensive chat response:', error);
      return {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: 'I encountered an error while analyzing your data. Please try again.',
        timestamp: Date.now()
      };
    }
  }

  private createComprehensivePrompt(message: string, userData: ComprehensiveUserData): string {
    const today = new Date();
    const todayPomodoros = userData.pomodoros.filter(p => {
      const pomodoroDate = new Date(p.startTime);
      return pomodoroDate.toDateString() === today.toDateString();
    });

    const thisWeekPomodoros = userData.pomodoros.filter(p => {
      const pomodoroDate = new Date(p.startTime);
      const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
      return pomodoroDate >= weekAgo;
    });

    const incompleteTasks = userData.tasks.filter(t => t.status !== 'completed');
    const highPriorityTasks = incompleteTasks.filter(t => t.priority === 'high' || t.priority === 'urgent');

    return `You are an advanced productivity assistant for PomodroMax with access to comprehensive user data. Here's the complete user profile:

=== CURRENT STATUS ===
- Timer Phase: ${userData.currentTimerState.phase}
- Timer Active: ${userData.currentTimerState.isActive}
- Completed Pomodoros Today: ${todayPomodoros.length}
- Completed Pomodoros This Week: ${thisWeekPomodoros.length}

=== PRODUCTIVITY STATISTICS ===
- Total Focus Time: ${Math.round(userData.userStats.totalFocusTime / 60)} minutes
- Total Completed Pomodoros: ${userData.userStats.completedPomodoros}
- Current Streak: ${userData.userStats.currentStreak} days
- Level: ${userData.userStats.level}
- Experience: ${userData.userStats.experience} XP
- This Week's Focus Time: ${Math.round(userData.weeklyStats.focusTime / 60)} minutes

=== PROJECTS (${userData.projects.length}) ===
${userData.projects.map(p => `- ${p.name} (${p.color}) - Created ${new Date(p.createdAt).toLocaleDateString()}`).join('\n') || 'No projects yet'}

=== TASKS (${userData.tasks.length} total, ${incompleteTasks.length} incomplete) ===
High Priority Tasks:
${highPriorityTasks.slice(0, 5).map(t => `- ${t.title} (${t.priority}) - Project: ${userData.projects.find(p => p.id === t.projectId)?.name || 'No Project'}`).join('\n') || 'No high priority tasks'}

Recent Tasks:
${userData.tasks.slice(0, 5).map(t => `- ${t.title} (${t.status}) - ${t.priority} priority`).join('\n')}

=== RECENT POMODOROS (${userData.pomodoros.length} total) ===
Today's Sessions:
${todayPomodoros.slice(0, 5).map(p => {
  const date = new Date(p.startTime);
  const duration = Math.round(p.duration / 60);
  return `- ${date.toLocaleTimeString()} - ${duration}min (${p.completed ? 'completed' : 'incomplete'}) - ${p.title}`;
}).join('\n') || 'No pomodoros today'}

This Week's Sessions:
${thisWeekPomodoros.slice(0, 10).map(p => {
  const date = new Date(p.startTime);
  const duration = Math.round(p.duration / 60);
  return `- ${date.toLocaleDateString()} ${date.toLocaleTimeString()} - ${duration}min - ${p.title}`;
}).join('\n') || 'No pomodoros this week'}

=== GOALS (${userData.goals.length}) ===
${userData.goals.map(g => `- ${g.title} (${g.type}) - ${g.current}/${g.target} ${g.period} - ${Math.round((g.current / g.target) * 100)}% complete`).join('\n') || 'No goals set'}

=== ACHIEVEMENTS (${userData.achievements.length}) ===
${userData.achievements.slice(0, 5).map(a => `- ${a.name} - Unlocked ${new Date(a.unlockedAt).toLocaleDateString()}`).join('\n') || 'No achievements yet'}

=== SOCIAL CONNECTIONS ===
- Friends: ${userData.friends.length}
- Pending Friend Requests: ${userData.friendRequests.length}
- Community Posts: ${userData.communityPosts.length}
- Shared Goals: ${userData.sharedGoals.length}

=== TIMER SETTINGS ===
- Work Duration: ${userData.settings.timer.pomodoro} minutes
- Short Break: ${userData.settings.timer.shortBreak} minutes
- Long Break: ${userData.settings.timer.longBreak} minutes
- Long Break Interval: Every ${userData.settings.timer.longBreakInterval} pomodoros

=== USER QUESTION ===
"${message}"

Provide a comprehensive, data-driven response based on ALL the user's actual data. Be specific about their current state, patterns, and provide actionable insights. Use their real project names, task titles, and statistics. Do NOT use any markdown formatting — no bold (**), no italic (*), no headers (#), no bullet points (-). Write in plain conversational text only.`;
  }

  private generateBasicComprehensiveResponse(message: string, userData: ComprehensiveUserData): ChatMessage {
    const lowercaseMessage = message.toLowerCase();
    let content = '';

    const todayPomodoros = userData.pomodoros.filter(p => {
      const pomodoroDate = new Date(p.startTime);
      return pomodoroDate.toDateString() === new Date().toDateString();
    });

    const incompleteTasks = userData.tasks.filter(t => t.status !== 'completed');
    const highPriorityTasks = incompleteTasks.filter(t => t.priority === 'high' || t.priority === 'urgent');

    if (lowercaseMessage.includes('project')) {
      if (userData.projects.length > 0) {
        content = `You have ${userData.projects.length} projects: ${userData.projects.map(p => p.name).join(', ')}. `;
        if (incompleteTasks.length > 0) {
          content += `You have ${incompleteTasks.length} tasks across these projects, including ${highPriorityTasks.length} high-priority ones.`;
        }
      } else {
        content = "You don't have any projects set up yet. Based on your tasks, I suggest creating projects to better organize your work.";
      }
    } else if (lowercaseMessage.includes('task')) {
      if (incompleteTasks.length > 0) {
        content = `You have ${incompleteTasks.length} incomplete tasks. `;
        if (highPriorityTasks.length > 0) {
          content += `Your high-priority tasks are: ${highPriorityTasks.slice(0, 3).map(t => t.title).join(', ')}. `;
        }
        content += `You've completed ${todayPomodoros.length} pomodoros today. I recommend focusing on your high-priority tasks first.`;
      } else {
        content = "Great job! You don't have any incomplete tasks. You've completed ${todayPomodoros.length} pomodoros today.";
      }
    } else if (lowercaseMessage.includes('pomodoro') || lowercaseMessage.includes('focus')) {
      content = `You've completed ${todayPomodoros.length} pomodoros today and ${userData.userStats.completedPomodoros} total. `;
      if (userData.userStats.currentStreak > 0) {
        content += `You're on a ${userData.userStats.currentStreak}-day streak! `;
      }
      content += `Your total focus time is ${Math.round(userData.userStats.totalFocusTime / 60)} minutes.`;
    } else if (lowercaseMessage.includes('goal')) {
      if (userData.goals.length > 0) {
        content = `You have ${userData.goals.length} active goals. `;
        const progressGoals = userData.goals.map(g => `${g.title} (${Math.round((g.current / g.target) * 100)}% complete)`);
        content += `Progress: ${progressGoals.join(', ')}.`;
      } else {
        content = "You don't have any goals set up yet. Setting goals can help you stay focused and track your progress.";
      }
    } else if (lowercaseMessage.includes('achievement')) {
      if (userData.achievements.length > 0) {
        content = `You've unlocked ${userData.achievements.length} achievements! Recent ones: ${userData.achievements.slice(0, 3).map(a => a.name).join(', ')}.`;
      } else {
        content = "You haven't unlocked any achievements yet. Keep completing pomodoros and tasks to earn your first achievement!";
      }
    } else {
      content = `Here's your productivity summary: ${userData.projects.length} projects, ${incompleteTasks.length} incomplete tasks, ${todayPomodoros.length} pomodoros today, ${userData.userStats.currentStreak} day streak, Level ${userData.userStats.level}. What would you like to focus on?`;
    }

    return {
      id: crypto.randomUUID(),
      role: 'assistant',
      content,
      timestamp: Date.now()
    };
  }

  async generateChatResponse(
    message: string,
    context: ChatContext
  ): Promise<ChatMessage> {
    // Debug logging
    console.log('🔍 DEBUG: AI Service received context:', {
      projectsCount: context.projects?.length || 0,
      projects: context.projects,
      incompleteTasksCount: context.incompleteTasks?.length || 0,
      recentPomodorosCount: context.recentPomodoros?.length || 0
    });
    
    // Create comprehensive prompt with user data, with safe fallbacks
    const timerState = context.currentTimerState || { phase: 'work', isActive: false, completedPomodoros: 0 };
    const timerSettings = context.timerSettings || { workDuration: 25, shortBreakDuration: 5, longBreakDuration: 15, longBreakInterval: 4 };
    
    const prompt = `You are a helpful productivity assistant for PomodroMax. Here's the user's current data:

CURRENT TIMER STATE:
- Phase: ${timerState.phase}
- Active: ${timerState.isActive}
- Completed Pomodoros Today: ${timerState.completedPomodoros}

TIMER SETTINGS:
- Work Duration: ${timerSettings.workDuration} minutes
- Short Break: ${timerSettings.shortBreakDuration} minutes
- Long Break: ${timerSettings.longBreakDuration} minutes
- Long Break Interval: Every ${timerSettings.longBreakInterval} pomodoros

PROJECTS:
${context.projects?.map(p => `- ${p.name}`).join('\n') || 'No projects yet'}

INCOMPLETE TASKS (${context.incompleteTasks?.length || 0}):
${context.incompleteTasks?.slice(0, 5).map(t => `- ${t.title} (${t.priority || 'medium'} priority)`).join('\n') || 'No incomplete tasks'}

RECENT POMODOROS (${context.recentPomodoros?.length || 0}):
${context.recentPomodoros?.slice(0, 5).map(p => {
  const date = new Date(p.startTime);
  const duration = Math.round(p.duration / 60);
  return `- ${date.toLocaleTimeString()} - ${duration}min (${p.completed ? 'completed' : 'incomplete'})`;
}).join('\n') || 'No recent pomodoros'}

USER QUESTION: "${message}"

Provide a helpful, specific response based on their actual data. Be concise but informative.`;

    if (this.apiKey && this.userId) {
      try {
        const response = await this.callDeepSeekAPI(prompt);
        return {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: response,
          timestamp: Date.now()
        };
      } catch (error) {
        console.error('AI service error:', error);
        return this.generateBasicChatResponse(message, context);
      }
    }
    
    return this.generateBasicChatResponse(message, context);
  }

  async generateTaskSuggestions(
    tasks: Task[],
    patterns: ProductivityPattern
  ): Promise<TaskSuggestion[]> {
    const prompt = `
      Analyze these tasks and patterns to suggest optimal task scheduling:
      Tasks: ${JSON.stringify(tasks)}
      Patterns: ${JSON.stringify(patterns)}

      Provide suggestions in this format:
      [
        {
          "title": string,
          "priority": "high" | "medium" | "low",
          "estimatedPomodoros": number,
          "bestTimeSlot": string,
          "reasoning": string
        }
      ]

      Consider:
      1. Task priorities and deadlines
      2. User's peak productivity hours
      3. Past completion patterns
      4. Task dependencies
      5. Energy levels throughout the day
    `;

    return this.getAIResponse(prompt, () => this.generateBasicTaskSuggestions(tasks, patterns));
  }

  // Combined method to get all insights in one API call
  async getAllInsights(
    pomodoros: Pomodoro[],
    tasks: Task[],
    projectNames?: Map<string, string>
  ): Promise<{
    analytics: AdvancedAnalytics;
    focusData: FocusAnalysis;
    focusZones: FocusZone[];
    breakSuggestion: BreakSuggestion | null;
    taskSuggestions: TaskSuggestion[];
  }> {
    const currentHour = new Date().getHours();
    const currentSession = pomodoros.length > 0 ? pomodoros[pomodoros.length - 1] : null;
    const recentSessions = pomodoros.slice(-5);

    // Ultra-aggressive optimization - minimal data only
    const maxPomodoros = 20; // Further reduced to 20 pomodoros
    const recentPomodoros = pomodoros.slice(-maxPomodoros);
    
    // Create ultra-minimal summary
    const pomodoroSummary = {
      totalCount: pomodoros.length,
      recentCount: recentPomodoros.length,
      averageDuration: Math.round(recentPomodoros.reduce((sum, p) => sum + p.duration, 0) / recentPomodoros.length || 0),
      completionRate: Math.round((recentPomodoros.filter(p => p.completed).length / recentPomodoros.length || 0) * 100) / 100,
      topProjects: this.getTopProjects(recentPomodoros, 3, projectNames), // Only top 3 projects
      topHours: this.getTopHours(recentPomodoros, 5), // Only top 5 hours
      lastSession: currentSession ? {
        duration: currentSession.duration,
        completed: currentSession.completed,
        projectId: currentSession.projectId
      } : null
    };

    const taskSummary = {
      totalCount: tasks.length,
      recentTasks: tasks.slice(-5).map(t => ({ // Reduced to 5 tasks
        title: t.title.substring(0, 50), // Truncate long titles
        priority: t.priority,
        completed: t.completed
      }))
    };

    const prompt = `
      Analyze this productivity data comprehensively and provide all insights in one response:
      
      Pomodoro Summary: ${JSON.stringify(pomodoroSummary)}
      Task Summary: ${JSON.stringify(taskSummary)}
      Current Session: ${JSON.stringify(currentSession)}
      Current Hour: ${currentHour}

      Return a comprehensive analysis in this JSON format:
      {
        "analytics": {
          "productivityScore": number,
          "focusQuality": number,
          "taskEfficiency": number,
          "workLifeBalance": number,
          "burnoutRisk": number,
          "suggestions": string[]
        },
        "focusData": {
          "overallQuality": number,
          "distractionPatterns": {
            "timeOfDay": [{"hour": number, "distractions": number}],
            "commonInterruptions": [{"type": string, "count": number}],
            "focusStreaks": [{"length": number, "frequency": number}]
          },
          "recommendations": {
            "timeBlocks": [{"start": number, "end": number, "quality": number}],
            "environmentalFactors": string[],
            "techniques": string[]
          }
        },
        "focusZones": [{
          "startHour": number,
          "endHour": number,
          "successRate": number,
          "distractionLevel": "low" | "medium" | "high",
          "energyLevel": "peak" | "moderate" | "low",
          "recommendedActivities": string[]
        }],
        "breakSuggestion": {
          "duration": number,
          "type": "short" | "long",
          "activity": string,
          "reason": string,
          "energyImpact": number
        } | null,
        "taskSuggestions": [{
          "title": string,
          "priority": "high" | "medium" | "low",
          "estimatedPomodoros": number,
          "bestTimeSlot": string,
          "reasoning": string
        }]
      }
    `;

    return this.getAIResponse(prompt, () => ({
      analytics: this.generateBasicAnalytics(pomodoros, tasks),
      focusData: this.generateBasicFocusAnalysis(pomodoros),
      focusZones: this.generateBasicFocusZones(pomodoros),
      breakSuggestion: this.generateBasicBreakSuggestion(currentSession, recentSessions, currentHour),
      taskSuggestions: this.generateBasicTaskSuggestions(tasks, this.generateBasicProductivityPattern(pomodoros, tasks))
    }));
  }

  // Helper methods for data summarization
  private getProjectDistribution(pomodoros: Pomodoro[]): Record<string, number> {
    const distribution: Record<string, number> = {};
    pomodoros.forEach(p => {
      const projectId = p.projectId || 'No Project';
      distribution[projectId] = (distribution[projectId] || 0) + 1;
    });
    return distribution;
  }

  private getHourlyDistribution(pomodoros: Pomodoro[]): Record<number, number> {
    const distribution: Record<number, number> = {};
    pomodoros.forEach(p => {
      const hour = new Date(p.startTime).getHours();
      distribution[hour] = (distribution[hour] || 0) + 1;
    });
    return distribution;
  }

  private getTopProjects(pomodoros: Pomodoro[], limit: number, projectNames?: Map<string, string>): Array<{project: string, count: number}> {
    const distribution = this.getProjectDistribution(pomodoros);
    return Object.entries(distribution)
      .sort(([,a], [,b]) => b - a)
      .slice(0, limit)
      .map(([projectId, count]) => ({
        project: projectNames?.get(projectId) || projectId,
        count,
      }));
  }

  private getTopHours(pomodoros: Pomodoro[], limit: number): Array<{hour: number, count: number}> {
    const distribution = this.getHourlyDistribution(pomodoros);
    return Object.entries(distribution)
      .sort(([,a], [,b]) => b - a)
      .slice(0, limit)
      .map(([hour, count]) => ({ hour: parseInt(hour), count }));
  }

  // Fallback methods for when AI is unavailable
  private generateBasicProductivityPattern(_pomodoros: Pomodoro[], _tasks: Task[]): ProductivityPattern {
    return {
      peakHours: [
        { hour: 9, productivity: 85 },
        { hour: 10, productivity: 90 },
        { hour: 15, productivity: 80 }
      ],
      bestDays: [
        { day: 'Monday', productivity: 85 },
        { day: 'Wednesday', productivity: 82 },
        { day: 'Thursday', productivity: 78 }
      ],
      taskCompletion: [
        { category: 'Work', rate: 75 },
        { category: 'Study', rate: 80 },
        { category: 'Personal', rate: 65 }
      ],
      focusPatterns: [
        { pattern: 'Morning Sessions', effectiveness: 85 },
        { pattern: 'Post-lunch Sessions', effectiveness: 70 },
        { pattern: 'Evening Sessions', effectiveness: 75 }
      ]
    };
  }

  private generateBasicAnalytics(_pomodoros: Pomodoro[], _tasks: Task[]): AdvancedAnalytics {
    return {
      productivityScore: 75,
      focusQuality: 80,
      taskEfficiency: 70,
      workLifeBalance: 65,
      burnoutRisk: 25,
      suggestions: [
        "Try to start your day with the most challenging tasks",
        "Take regular breaks to maintain focus",
        "Consider grouping similar tasks together"
      ]
    };
  }

  private generateBasicFocusAnalysis(_pomodoros: Pomodoro[]): FocusAnalysis {
    return {
      overallQuality: 75,
      distractionPatterns: {
        timeOfDay: [
          { hour: 9, distractions: 1 },
          { hour: 14, distractions: 3 },
          { hour: 16, distractions: 2 }
        ],
        commonInterruptions: [
          { type: 'Notifications', count: 5 },
          { type: 'Noise', count: 3 },
          { type: 'Meetings', count: 2 }
        ],
        focusStreaks: [
          { length: 25, frequency: 4 },
          { length: 50, frequency: 2 },
          { length: 75, frequency: 1 }
        ]
      },
      recommendations: {
        timeBlocks: [
          { start: 9, end: 11, quality: 0.85 },
          { start: 14, end: 16, quality: 0.75 },
          { start: 16, end: 18, quality: 0.7 }
        ],
        environmentalFactors: [
          "Minimize notifications during focus periods",
          "Find a quiet workspace",
          "Ensure proper lighting"
        ],
        techniques: [
          "Use noise-canceling headphones",
          "Try the 2-minute rule for small tasks",
          "Schedule focused work during peak hours"
        ]
      }
    };
  }

  private generateBasicBreakSuggestion(
    currentSession: Pomodoro | null,
    recentSessions: Pomodoro[],
    _currentHour: number
  ): BreakSuggestion | null {
    if (!currentSession && recentSessions.length === 0) {
      return null;
    }

    return {
      duration: 5,
      type: 'short',
      activity: "Quick stretching and deep breathing",
      reason: "Regular short breaks help maintain focus and prevent fatigue",
      energyImpact: 0.7
    };
  }

  private generateBasicFocusZones(_pomodoros: Pomodoro[]): FocusZone[] {
    return [
      {
        startHour: 9,
        endHour: 11,
        successRate: 0.85,
        distractionLevel: 'low',
        energyLevel: 'peak',
        recommendedActivities: [
          "Complex problem solving",
          "Creative work",
          "Important meetings"
        ]
      },
      {
        startHour: 14,
        endHour: 16,
        successRate: 0.75,
        distractionLevel: 'medium',
        energyLevel: 'moderate',
        recommendedActivities: [
          "Code reviews",
          "Documentation",
          "Team collaboration"
        ]
      }
    ];
  }

  private generateBasicChatResponse(message: string, context: ChatContext): ChatMessage {
    const lowercaseMessage = message.toLowerCase();
    let content = '';

    // Safe fallbacks for undefined values
    const timerState = context.currentTimerState || { phase: 'work', isActive: false, completedPomodoros: 0 };
    const timerSettings = context.timerSettings || { workDuration: 25, shortBreakDuration: 5, longBreakDuration: 15, longBreakInterval: 4 };
    const incompleteTasks = context.incompleteTasks || [];
    const projects = context.projects || [];
    const recentPomodoros = context.recentPomodoros || [];

    if (lowercaseMessage.includes('task') || lowercaseMessage.includes('work')) {
      if (incompleteTasks.length > 0) {
        const highPriorityTasks = incompleteTasks.filter(t => t.priority === 'high' || t.priority === 'urgent');
        if (highPriorityTasks.length > 0) {
          content = `You have ${incompleteTasks.length} tasks pending, including ${highPriorityTasks.length} high-priority ones. I recommend starting with: "${highPriorityTasks[0].title}". Your timer is set to ${timerSettings.workDuration} minutes for focused work.`;
        } else {
          content = `You have ${incompleteTasks.length} tasks pending. I suggest starting with: "${incompleteTasks[0].title}". Your timer is set to ${timerSettings.workDuration} minutes for focused work.`;
        }
      } else {
        content = "You don't have any active tasks. Would you like help creating a new task?";
      }
    } else if (lowercaseMessage.includes('break') || lowercaseMessage.includes('rest')) {
      const nextBreakType = timerState.completedPomodoros % timerSettings.longBreakInterval === 0 ? 'long' : 'short';
      const breakDuration = nextBreakType === 'long' ? timerSettings.longBreakDuration : timerSettings.shortBreakDuration;
      content = `You've completed ${timerState.completedPomodoros} pomodoros today. Your next break should be a ${nextBreakType} break (${breakDuration} minutes). This helps maintain focus and prevent burnout.`;
    } else if (lowercaseMessage.includes('focus') || lowercaseMessage.includes('concentrate')) {
      content = `Your timer is currently in ${timerState.phase} phase and ${timerState.isActive ? 'running' : 'paused'}. You've completed ${timerState.completedPomodoros} pomodoros today. Keep up the great work!`;
    } else if (lowercaseMessage.includes('productive') || lowercaseMessage.includes('efficiency')) {
      const recentPomodorosSlice = recentPomodoros.slice(0, 3);
      if (recentPomodorosSlice.length > 0) {
        const avgDuration = Math.round(recentPomodorosSlice.reduce((sum, p) => sum + p.duration, 0) / recentPomodorosSlice.length / 60);
        content = `You've completed ${timerState.completedPomodoros} pomodoros today. Your recent sessions averaged ${avgDuration} minutes. You have ${incompleteTasks.length} tasks to work on.`;
      } else {
        content = `You have ${incompleteTasks.length} tasks to work on. Start with a ${timerSettings.workDuration}-minute pomodoro session to build momentum.`;
      }
    } else if (lowercaseMessage.includes('project')) {
      if (projects.length > 0) {
        content = `You have ${projects.length} projects: ${projects.map(p => p.name).join(', ')}. You have ${incompleteTasks.length} tasks across these projects.`;
      } else {
        content = "You don't have any projects set up yet. Would you like help creating your first project?";
      }
    } else {
      content = `I can help with your productivity! You have ${incompleteTasks.length} tasks pending, ${projects.length} projects, and have completed ${timerState.completedPomodoros} pomodoros today. What would you like to focus on?`;
    }

    return {
      id: crypto.randomUUID(),
      role: 'assistant',
      content,
      timestamp: Date.now()
    };
  }

  private generateBasicTaskSuggestions(tasks: Task[], patterns: ProductivityPattern): TaskSuggestion[] {
    if (tasks.length === 0) {
      return [
        {
          title: "Create your first task",
          priority: "high",
          estimatedPomodoros: 1,
          bestTimeSlot: "Morning (9 AM - 11 AM)",
          reasoning: "Getting started with task management will help you track and improve your productivity"
        },
        {
          title: "Set up daily goals",
          priority: "medium",
          estimatedPomodoros: 2,
          bestTimeSlot: "Early afternoon (1 PM - 3 PM)",
          reasoning: "Having clear daily goals helps maintain focus and measure progress"
        },
        {
          title: "Plan your week ahead",
          priority: "medium",
          estimatedPomodoros: 3,
          bestTimeSlot: "Evening (4 PM - 6 PM)",
          reasoning: "Weekly planning ensures better time management and reduces daily decision fatigue"
        }
      ];
    }

    const incompleteTasks = tasks.filter(t => !t.completed);
    if (incompleteTasks.length === 0) {
      return [
        {
          title: "Review completed tasks",
          priority: "medium",
          estimatedPomodoros: 1,
          bestTimeSlot: "Afternoon (2 PM - 4 PM)",
          reasoning: "Analyzing completed tasks helps improve future estimations and identify patterns"
        },
        {
          title: "Plan next milestone",
          priority: "high",
          estimatedPomodoros: 2,
          bestTimeSlot: "Morning (10 AM - 12 PM)",
          reasoning: "Setting new goals maintains momentum and continuous improvement"
        }
      ];
    }

    return incompleteTasks.slice(0, 3).map(task => ({
      title: task.title,
      priority: (task.priority === 'urgent' ? 'high' : task.priority === 'none' ? 'medium' : task.priority) as 'low' | 'medium' | 'high',
      estimatedPomodoros: task.estimatedPomodoros || 2,
      bestTimeSlot: this.suggestTimeSlot(task.title, patterns.peakHours.map(h => h.hour), patterns.bestDays.map(d => d.day)),
      reasoning: `This task aligns with your current goals and energy levels. ${
        patterns.peakHours.length > 0 
          ? `Your productivity peaks at ${patterns.peakHours[0].hour}:00.`
          : ''
      }`
    }));
  }

  private suggestTimeSlot(title: string, peakHours: number[], _bestDays: string[]): string {
    const peakHour = peakHours[0] || 9;
    const peakHourTime = `${peakHour}:00`;

    if (title.toLowerCase().includes('morning')) {
      return peakHourTime;
    } else if (title.toLowerCase().includes('afternoon')) {
      return peakHourTime;
    } else if (title.toLowerCase().includes('evening')) {
      return peakHourTime;
    } else if (title.toLowerCase().includes('night')) {
      return peakHourTime;
    } else {
      return peakHourTime;
    }
  }

  getRemainingUsage(): number {
    if (!this.userId) return 0;
    return pricingService.getRemainingUsage(this.userId);
  }

  async upgradeTier(newTier: string): Promise<boolean> {
    if (!this.userId) return false;
    return pricingService.upgradeTier(this.userId, newTier);
  }

  getUsageStats(): UsageStats | null {
    if (!this.userId) return null;
    return pricingService.getUsageStats(this.userId);
  }
}

export const aiService = new AIService();
export type { 
  BreakSuggestion, 
  FocusZone, 
  ProductivityPattern, 
  TaskSuggestion, 
  AdvancedAnalytics, 
  FocusAnalysis,
  ChatMessage,
  ChatContext 
};
export { AIService };